import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Loader2, Clock, XCircle, Crown, Zap, Star, ArrowRight, CreditCard, Download, GraduationCap, ShieldCheck } from "lucide-react";
import { pollEstadoPago, fetchMiMembresia, fetchMiLicencia } from "../lib/pagos";
import { PlanBadge } from "../components/PlanBadge";

const PLAN_META = {
  pro: { label: "Pro", Icon: Zap, color: "#2563EB" },
  elite: { label: "Premium", Icon: Crown, color: "#B45309" },
};

function fmtFecha(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-PE", { day: "2-digit", month: "long", year: "numeric" });
}

export default function PagoExito() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const tipo = params.get("tipo") || "membresia";
  const pub = params.get("pub");
  const slug = params.get("slug");
  const started = useRef(false);
  const [estado, setEstado] = useState("verificando"); // verificando | exito | pendiente | error
  const [membresia, setMembresia] = useState(null);
  const [licencia, setLicencia] = useState(null);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!sessionId) { setEstado("error"); return; }
    (async () => {
      try {
        const r = await pollEstadoPago(sessionId);
        if (r.payment_status === "paid") {
          if (tipo === "membresia") {
            try { setMembresia(await fetchMiMembresia()); }
            catch (err) { console.error("[PagoExito] no se pudo cargar la membresía tras el pago:", err); }
          } else if (tipo === "licencia" && slug) {
            try { setLicencia(await fetchMiLicencia(slug)); }
            catch (err) { console.error("[PagoExito] no se pudo cargar la licencia tras el pago:", err); }
          }
          setEstado("exito");
        } else if (["pending", "in_process", "timeout"].includes(r.payment_status) || r.status === "pending") {
          setEstado("pendiente");
        } else {
          setEstado("error");
        }
      } catch {
        setEstado("error");
      }
    })();
    // eslint-disable-next-line
  }, []);

  const planKey = membresia?.plan;
  const planMeta = PLAN_META[planKey];

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-5 py-12" data-testid="pago-exito-page">
      <div className="w-full max-w-lg">
        {estado === "verificando" && (
          <div className="text-center" data-testid="pago-verificando">
            <Loader2 size={40} className="mx-auto animate-spin text-brand-blue" />
            <p className="mt-4 text-slate-600 font-medium">Confirmando tu pago con Mercado Pago…</p>
            <p className="mt-1 text-sm text-slate-400">Esto toma solo unos segundos.</p>
          </div>
        )}

        {estado === "exito" && (
          <div className="bg-white rounded-3xl border border-slate-100 shadow-lifted overflow-hidden" data-testid="pago-exito">
            <div className="relative bg-gradient-to-br from-emerald-500 to-emerald-600 px-8 pt-10 pb-14 text-center">
              <div className="pago-check-ring mx-auto">
                <div className="pago-check-circle">
                  <Check size={44} className="text-emerald-600 pago-check-icon" strokeWidth={3} />
                </div>
              </div>
              <h1 className="mt-6 text-2xl font-extrabold text-white">¡Pago confirmado!</h1>
              <p className="mt-1 text-emerald-50 text-sm">Gracias por ser parte de la Familia ConstruLink.</p>
            </div>

            <div className="px-8 py-8 -mt-8">
              <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-5">
                {tipo === "membresia" ? (
                  <>
                    <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Resumen</p>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-slate-500 text-sm">Plan activado</span>
                      <span className="inline-flex items-center gap-2 font-bold text-slate-900" data-testid="pago-plan">
                        {planMeta ? <planMeta.Icon size={16} style={{ color: planMeta.color }} /> : null}
                        {planMeta?.label || planKey || "—"}
                        <PlanBadge plan={planKey} />
                      </span>
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                      <span className="text-slate-500 text-sm">Válido hasta</span>
                      <span className="font-bold text-slate-900" data-testid="pago-vencimiento">{fmtFecha(membresia?.vigente_hasta)}</span>
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 text-emerald-600 text-sm font-medium">
                      <CreditCard size={15} /> Renovación mensual · Mercado Pago
                    </div>
                  </>
                ) : tipo === "producto" ? (
                  <div className="flex items-center gap-3" data-testid="pago-producto">
                    <span className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <Download size={22} className="text-emerald-600" />
                    </span>
                    <div>
                      <p className="font-bold text-slate-900">¡Producto adquirido!</p>
                      <p className="text-sm text-slate-500">Ya está disponible en tu biblioteca "Mis Descargas".</p>
                    </div>
                  </div>
                ) : tipo === "curso" ? (
                  <div className="flex items-center gap-3" data-testid="pago-curso">
                    <span className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#EA580C1a" }}>
                      <GraduationCap size={22} style={{ color: "#EA580C" }} />
                    </span>
                    <div>
                      <p className="font-bold text-slate-900">¡Inscripción confirmada!</p>
                      <p className="text-sm text-slate-500">Ya puedes ver tu curso en "Mis Cursos".</p>
                    </div>
                  </div>
                ) : tipo === "licencia" ? (
                  <div data-testid="pago-licencia">
                    <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Licencia activada</p>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-slate-500 text-sm">Código</span>
                      <span className="font-mono font-bold text-slate-900" data-testid="pago-licencia-codigo">{licencia?.codigo || "—"}</span>
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                      <span className="text-slate-500 text-sm">Duración</span>
                      <span className="font-bold text-slate-900">{licencia?.duracion_meses ? `${licencia.duracion_meses} meses` : "—"}</span>
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                      <span className="text-slate-500 text-sm">Válida hasta</span>
                      <span className="font-bold text-slate-900" data-testid="pago-licencia-vence">{fmtFecha(licencia?.fecha_vencimiento)}</span>
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 text-emerald-600 text-sm font-medium">
                      <ShieldCheck size={15} /> Acceso completo al programa durante la vigencia
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <span className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <Star size={22} className="text-amber-500 fill-amber-500" />
                    </span>
                    <div>
                      <p className="font-bold text-slate-900">¡Publicación destacada!</p>
                      <p className="text-sm text-slate-500">Ya aparece primero en los resultados de la Red.</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 flex flex-col gap-2.5">
                {tipo === "membresia" ? (
                  <Link to="/planes" data-testid="pago-ir-planes" className="w-full inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-blue text-white font-semibold py-3 rounded-xl transition-colors">
                    Ver mi membresía <ArrowRight size={16} />
                  </Link>
                ) : tipo === "producto" ? (
                  <Link to="/software/mis-descargas" data-testid="pago-ir-descargas" className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-xl transition-colors">
                    Ir a Mis Descargas <ArrowRight size={16} />
                  </Link>
                ) : tipo === "curso" ? (
                  <Link to="/academia/mis-cursos" data-testid="pago-ir-cursos" className="w-full inline-flex items-center justify-center gap-2 text-white font-semibold py-3 rounded-xl transition-colors" style={{ backgroundColor: "#EA580C" }}>
                    Ir a Mis Cursos <ArrowRight size={16} />
                  </Link>
                ) : tipo === "licencia" ? (
                  <Link to={slug === "vivienda-multifamiliar" ? "/software/vivienda-multifamiliar" : "/software"} data-testid="pago-ir-programa" className="w-full inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 rounded-xl transition-colors">
                    ENTRAR AL PROGRAMA <ArrowRight size={16} />
                  </Link>
                ) : (
                  <Link to={pub ? `/conexion/publicaciones/${pub}` : "/conexion"} data-testid="pago-ver-pub" className="w-full inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-blue text-white font-semibold py-3 rounded-xl transition-colors">
                    Ver mi publicación <ArrowRight size={16} />
                  </Link>
                )}
                <Link to="/" className="w-full text-center text-sm font-semibold text-slate-500 hover:text-slate-800 py-2">Volver al Ecosistema</Link>
              </div>
            </div>
          </div>
        )}

        {estado === "pendiente" && (
          <div className="text-center bg-white rounded-3xl border border-slate-100 shadow-soft p-10" data-testid="pago-pendiente">
            <Clock size={44} className="mx-auto text-amber-500" />
            <h1 className="mt-4 text-xl font-extrabold text-slate-900">Pago en proceso</h1>
            <p className="mt-2 text-slate-500 text-sm">Tu pago está siendo procesado (p. ej. PagoEfectivo puede tardar). Te activaremos automáticamente al confirmarse.</p>
            <Link to="/planes" className="mt-6 inline-flex items-center gap-2 text-brand-blue font-semibold hover:underline">Ir a mis planes <ArrowRight size={15} /></Link>
          </div>
        )}

        {estado === "error" && (
          <div className="text-center bg-white rounded-3xl border border-slate-100 shadow-soft p-10" data-testid="pago-error">
            <XCircle size={44} className="mx-auto text-brand-red" />
            <h1 className="mt-4 text-xl font-extrabold text-slate-900">No pudimos confirmar el pago</h1>
            <p className="mt-2 text-slate-500 text-sm">Si el importe fue descontado, se activará al confirmarse. Puedes reintentar desde tus planes.</p>
            <Link to="/planes" className="mt-6 inline-flex items-center gap-2 text-brand-blue font-semibold hover:underline">Volver a intentar <ArrowRight size={15} /></Link>
          </div>
        )}
      </div>
    </div>
  );
}
